import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { inr } from "@/lib/format";
import { Loader2, ShoppingBag, Zap, ChevronDown } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        setColor(r.data.colors?.[0] || null);
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;
  if (!product) return <p className="text-center py-32 font-mono text-xs uppercase tracking-widest text-zinc-500">Product not found</p>;

  const sizeObj = product.sizes?.find((s) => s.size === size);
  const canBuy = size && (!sizeObj || sizeObj.stock > 0);

  const buildItem = () => ({
    product_id: product.id,
    name: product.name,
    image: product.images?.[0],
    price: product.price,
    size,
    color,
    qty: 1,
  });

  const handleAdd = () => {
    if (!size) return toast.error("Please select a size");
    addItem(buildItem());
    toast.success("Added to bag");
  };

  const handleBuyNow = () => {
    if (!size) return toast.error("Please select a size");
    addItem(buildItem());
    navigate("/checkout");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10" data-testid="product-detail-page">
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-14">
        {/* Gallery */}
        <div className="space-y-3">
          <motion.div key={activeImg} initial={{ opacity: 0.4 }} animate={{ opacity: 1 }} className="aspect-[3/4] bg-zinc-900 overflow-hidden">
            <img src={product.images?.[activeImg]} alt={product.name} className="h-full w-full object-cover" data-testid="product-main-image" />
          </motion.div>
          {product.images?.length > 1 && (
            <div className="flex gap-3">
              {product.images.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`h-20 w-16 overflow-hidden border ${activeImg === i ? "border-brand" : "border-zinc-800"}`}>
                  <img src={img} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <p className="font-mono text-[11px] uppercase tracking-widest text-brand">{product.category}</p>
          <h1 className="font-display text-3xl lg:text-4xl font-extrabold uppercase tracking-tight mt-2">{product.name}</h1>
          <div className="flex items-center gap-3 mt-3">
            <span className="font-mono text-xl">{inr(product.price)}</span>
            {product.compare_at && <span className="font-mono text-sm text-zinc-600 line-through">{inr(product.compare_at)}</span>}
          </div>
          <p className="mt-5 text-sm text-zinc-400 leading-relaxed">{product.description}</p>

          {product.colors?.length > 0 && (
            <div className="mt-7">
              <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-3">Color · {color}</p>
              <div className="flex gap-2">
                {product.colors.map((c) => (
                  <button key={c} onClick={() => setColor(c)} data-testid={`color-${c}`} className={`px-3 py-1.5 font-mono text-[11px] uppercase tracking-widest border transition ${color === c ? "border-brand text-brand" : "border-zinc-800 text-zinc-400"}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-7">
            <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-3">Select Size</p>
            <div className="flex flex-wrap gap-2">
              {product.sizes?.map((s) => {
                const out = s.stock <= 0;
                return (
                  <button
                    key={s.size}
                    disabled={out}
                    onClick={() => setSize(s.size)}
                    data-testid={`size-${s.size}`}
                    className={`min-w-14 px-3 py-2.5 font-mono text-xs uppercase tracking-widest border transition ${
                      size === s.size ? "bg-brand text-black border-brand" : "border-zinc-800 hover:border-zinc-600"
                    } ${out ? "opacity-30 line-through cursor-not-allowed" : ""}`}
                  >
                    {s.size}
                  </button>
                );
              })}
            </div>
            {sizeObj && sizeObj.stock > 0 && sizeObj.stock <= 5 && (
              <p className="mt-2 font-mono text-[11px] uppercase tracking-widest text-red-400">Only {sizeObj.stock} left in {sizeObj.size}</p>
            )}
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleAdd}
              data-testid="add-to-cart-btn"
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-white/40 py-3.5 text-sm font-bold uppercase tracking-wide hover:border-brand hover:text-brand active:scale-95 transition"
            >
              <ShoppingBag className="h-4 w-4" /> Add to Bag
            </button>
            <button
              onClick={handleBuyNow}
              data-testid="buy-now-btn"
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-brand text-black py-3.5 text-sm font-bold uppercase tracking-wide hover:bg-brand-hover active:scale-95 transition"
            >
              <Zap className="h-4 w-4" /> Buy Now · UPI
            </button>
          </div>

          <Accordion type="single" collapsible className="mt-8 border-t border-zinc-900">
            <AccordionItem value="fabric" className="border-zinc-900">
              <AccordionTrigger className="font-mono text-xs uppercase tracking-widest">Fabric & Care</AccordionTrigger>
              <AccordionContent className="text-sm text-zinc-400">{product.fabric || "Premium materials, machine wash cold."}</AccordionContent>
            </AccordionItem>
            <AccordionItem value="ship" className="border-zinc-900">
              <AccordionTrigger className="font-mono text-xs uppercase tracking-widest">Shipping & Returns</AccordionTrigger>
              <AccordionContent className="text-sm text-zinc-400">Free shipping over ₹2,999. Dispatched in 24-48h. 7-day easy returns.</AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
    </div>
  );
}
